import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingModal } from './LoadingModal';
import { processReturn, clearError } from '../redux/slices/reservationSlice';

const ProcessReturnModal = ({
  setShowReturnModal,
  reservation,
  equipmentName,
  onReturnProcessed,
}) => {
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.reservations.loading);
  const error = useSelector((state) => state.reservations.error);
  const [formData, setFormData] = useState({
    returnCondition: '',
    hasDamage: false,
    damageDescription: '',
    damageImages: [],
    estimatedRepairCost: '',
    returnNotes: '',
    restoreQuantity: true,
  });

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          damageImages: [...prev.damageImages, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      damageImages: prev.damageImages.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const resultAction = await dispatch(
      processReturn({
        reservationId: reservation._id,
        returnData: formData,
      })
    ).unwrap();

    onReturnProcessed();
    setShowReturnModal(false);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={() => setShowReturnModal(false)}
      ></div>

      {(loading || error) && (
        <LoadingModal
          message="Processing return..."
          error={error}
          onClose={() => dispatch(clearError())}
        />
      )}

      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] my-4 mx-4 flex flex-col z-10">
        <div className="px-6 py-4 border-b">
          <h2 className="text-2xl font-bold">Process Equipment Return</h2>
          <div className="mb-1">
            <p className="font-semibold">Equipment: {equipmentName}</p>
            <p className="text-sm text-gray-600">
              Rental Quantity: {reservation.quantity || reservation.rentalTotal || 1}
            </p>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="returnForm" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-semibold mb-2">Condition Assessment</label>
              <select
                name="returnCondition"
                value={formData.returnCondition}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
                required
              >
                <option value="">Select condition...</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="damaged">Damaged</option>
              </select>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="hasDamage"
                  checked={formData.hasDamage}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span>Report Damage</span>
              </label>
            </div>

            {formData.hasDamage && (
              <>
                <div>
                  <label className="block font-semibold mb-2">Damage Description</label>
                  <textarea
                    name="damageDescription"
                    value={formData.damageDescription}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    rows="3"
                    required
                  />
                </div>

                <div>
                  <label className="block font-semibold mb-2">Damage Photos</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="w-full"
                  />
                  {formData.damageImages.length > 0 && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {formData.damageImages.map((img, index) => (
                        <div key={index} className="relative">
                          <img
                            src={img}
                            alt={`Damage ${index + 1}`}
                            className="w-full h-24 object-cover rounded"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-700"
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block font-semibold mb-2">Estimated Repair Cost</label>
                  <input
                    type="number"
                    name="estimatedRepairCost"
                    value={formData.estimatedRepairCost}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </>
            )}

            <div>
              <label className="block font-semibold mb-2">Additional Notes</label>
              <textarea
                name="returnNotes"
                value={formData.returnNotes}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
                rows="3"
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="restoreQuantity"
                  checked={formData.restoreQuantity}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span className="font-semibold">Restore equipment quantity to inventory</span>
              </label>
              <p className="text-sm text-gray-600 ml-6">
                Uncheck this if the equipment is damaged beyond repair or should not be returned to
                inventory
              </p>
            </div>
          </form>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => setShowReturnModal(false)}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="returnForm"
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              disabled={loading}
            >
              Process Return
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ProcessReturnModal };
